import type { Tagged } from "@-/com.npmjs.type-fest";
import { drillTypeCodec, type DrillType } from "@-/dev.improve.haniverse.lib.drill.entities";

import { type CardContent, cardContentDrillType, cardContentSubject } from "#card-content";

/**
 * What a card IS, as one string: its subject's text (a grapheme, or a word) plus the drill type it
 * is drilled as — `大` + `meaning`, `大` + `pronunciation-da4`, `学习` + `word-sentence-xue2xi2`.
 * Every kind of card uses this one scheme, so a card read from the actor DB, resolved from the
 * content DB, or named by a route compares equal exactly when it is the same card.
 *
 * Built only from the card's own identity (the text the learner studies and its tone-number
 * reading). Never from content-DB integer ids, which renumber on every content build and differ
 * between the online catalog and the offline subset, nor from glosses, labels or positions, which
 * content edits rewrite.
 */
export type CardIdentityKey = Tagged<string, "CardIdentityKey">;

export function cardIdentityKey({
  drillType,
  subjectText,
}: {
  drillType: DrillType;
  subjectText: string;
}): CardIdentityKey {
  // NUL cannot occur in subject text, so the two parts can never run together ambiguously.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- the one constructor of the tag
  return `${subjectText}\0${drillTypeCodec.encode(drillType)}` as CardIdentityKey;
}

/** A quiz card's identity: its catalogue key. It is never drilled, so it has no drill type. */
function quizCardIdentityKey(key: string): CardIdentityKey {
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- the quiz constructor of the tag
  return `${key}\0quiz` as CardIdentityKey;
}

export function cardContentIdentityKey(cardContent: CardContent): CardIdentityKey {
  if (cardContent.kind === "quiz") {
    return quizCardIdentityKey(cardContent.key);
  }
  return cardIdentityKey({
    drillType: cardContentDrillType(cardContent),
    subjectText: cardContentSubject(cardContent).text,
  });
}
